import { compare } from 'fast-json-patch';
import {
  buffers, type EventChannel, eventChannel,
} from 'redux-saga';
import {
  call, delay, put, race, take, takeEvery, takeLatest,
} from 'redux-saga/effects';
import type { DeepPartial } from 'ts-essentials';
import {
  type CreateReportEventBroadcastData,
  registerCreateReportEventsBroadcastListener,
} from '~/_shared/utils/api/broadcast.helpers';
import { downloadFile } from '~/_shared/utils/document/document.helpers';
import { noop } from '~/_shared/utils/function.helpers';
import { select } from '~/_shared/utils/saga/effects';
import { type PickAction } from '~/_shared/utils/types/action.type';
import { AppErrorType } from '~/appError/appErrorType.enum';
import {
  createPdfReport, getPdfReportStatus, type PdfReportCreateResponse, type PdfReportStatusResponse,
} from '~/exportReport/pdfReport.repository';
import type { CreateReportAction } from '~/store/frontendState/createReport/createReport.action';
import {
  createReportAddItem, createReportCreateRequest, createReportEventReceived, createReportUpdateItem,
} from '~/store/frontendState/createReport/createReport.actionCreators';
import { normalizePathForMapSettingsTree } from '~/store/mapSettings/data/mapSettingsData.helpers';
import type { MapSettingsDataState } from '~/store/mapSettings/data/mapSettingsData.state';
import { toolsStateExportReportSelector } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.selectors';
import type { ExportReportToolState } from '~/store/mapSettings/toolsState/exportReport/exportReportToolState.state';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { USER_WEB_SOCKET_BROADCAST_INITIALIZED } from '~/store/userData/userData.actionTypes';
import { translate } from '~/translations/Trans';

const POLLING_DELAY = 10_000;

export function* createReportSagas() {
  yield takeLatest(USER_WEB_SOCKET_BROADCAST_INITIALIZED, initializeWebSocketChannel);
  yield takeEvery(createReportAddItem.type, onCreateReportItemAdded);
  yield takeEvery(createReportCreateRequest.type, onCreateReportCreateRequest);
}

function* initializeWebSocketChannel() {
  // Create the main websocket channel that receives raw messages
  const webSocketsChannel: EventChannel<CreateReportListenerMessage> = yield call(eventChannel<CreateReportListenerMessage>, (emitter) => {
    registerCreateReportEventsBroadcastListener({
      // onEvent: (data) => emitter({ type: 'change', realSpreadsheetId: data.real_spreadsheet_id, data }),
      onFinished: (data) => emitter({ type: 'finished', data }),
      onError: () => emitter({ type: 'error' }),
    });

    return noop;
  }, buffers.expanding());

  while (true) {
    const message: CreateReportListenerMessage | undefined = yield take(webSocketsChannel);

    if (message) {
      switch (message.type) {
        case 'finished': {
          yield put(createReportUpdateItem({
            hash: message.data.hash,
            mapId: message.data.map_id,
            downloadUrl: message.data.path,
            status: 'Processed',
          }));

          downloadFile(message.data.path);
          break;
        }
        case 'error': {
          yield notifyCreateReportError();
          break;
        }
        default:
      }

      yield put(createReportEventReceived(message));
    }
  }
}

function* notifyCreateReportError() {
  yield put(createAppError({
    type: AppErrorType.General,
    title: translate('Error Exporting a Report'),
    text: translate('We were unable to export your report. Please try again. If this problem persists, please contact the customer support'),
  }));
}

function* onCreateReportCreateRequest(action: PickAction<CreateReportAction, typeof createReportCreateRequest.type>) {
  const exportPdfState: ExportReportToolState = yield select<ExportReportToolState>(toolsStateExportReportSelector);

  const initialToolsState: DeepPartial<MapSettingsDataState> = {
    toolsState: {},
  };

  const currentToolsState: DeepPartial<MapSettingsDataState> = {
    toolsState: {
      exportReport: exportPdfState,
    },
  };

  const diff = compare(initialToolsState, currentToolsState);
  const mappedDiff = normalizePathForMapSettingsTree(diff);
  const stringifiedMappedDiff = mappedDiff.map(item => JSON.stringify(item));

  try {
    const response: PdfReportCreateResponse = yield call(createPdfReport, action.payload.mapId, stringifiedMappedDiff);
    yield put(createReportAddItem({
      hash: response.hash,
      mapId: action.payload.mapId,
      status: 'Processing',
    }));
  }
  catch (error) {
    yield notifyCreateReportError();
  }
}

function* onCreateReportItemAdded(action: PickAction<CreateReportAction, typeof createReportAddItem.type>) {
  while (true) {
    const { messageAction }: { messageAction: ReturnType<typeof createReportEventReceived> | undefined } = yield race({
      messageAction: take(
        (reduxAction: Action) => (
          createReportEventReceived.match(reduxAction) &&
          reduxAction.payload.type === 'finished' && reduxAction.payload.data.hash === action.payload.item.hash
        ),
      ),
      timeout: delay(POLLING_DELAY),
    });

    const message: CreateReportListenerMessage | undefined = messageAction?.payload;

    if (message) {
      switch (message.type) {
        case 'finished': {
          if (message.data.code === 'FAILED') {
            yield notifyCreateReportError();
          }
          return;
        }
        default:
      }
    }
    else {
      try {
        const statusResponse: PdfReportStatusResponse = yield call(getPdfReportStatus, action.payload.item.mapId, action.payload.item.hash);
        if (statusResponse.code === 'FAILED') {
          yield notifyCreateReportError();
          return;
        }
        if (statusResponse.code === 'PROCESSED') {
          yield put(createReportUpdateItem({
            ...action.payload.item,
            downloadUrl: statusResponse.data?.path,
            status: 'Processed',
          }));

          if (statusResponse.data?.path) {
            downloadFile(statusResponse.data.path);
          }

          return;
        }
      }
      catch (e) {
        // fail gracefully
      }
    }
  }
}

export type CreateReportListenerMessage = {
  type: 'finished';
  data: CreateReportEventBroadcastData;
} | {
  type: 'error';
};
